import React, { useState, useEffect, useCallback } from 'react';
import { XMarkIcon } from '@heroicons/react/24/outline';
import { supabase } from '../services/supabase';
import { useAuth } from '../context/AuthContext';
import toast from 'react-hot-toast';

const CPAC_SUBMITTED_KEY = 'cpac_popup_submitted';
const CPAC_CLOSED_KEY = 'cpac_popup_closed_at';
const COUPON_CODE = 'CPAC25';

type Step = 'email' | 'phone' | 'event' | 'done';

const EVENT_TYPES = [
  { value: 'birthday', label: 'Birthday' },
  { value: 'graduation', label: 'Graduation' },
  { value: 'anniversary', label: 'Anniversary' },
  { value: 'announcement', label: 'Announcement' },
];

const safeGet = (key: string): string | null => {
  try { return localStorage.getItem(key); } catch { return null; }
};
const safeSet = (key: string, value: string): void => {
  try { localStorage.setItem(key, value); } catch {}
};

const CPACPopup: React.FC = () => {
  const { user } = useAuth();
  const [visible, setVisible] = useState(false);
  const [showFab, setShowFab] = useState(false);
  const [step, setStep] = useState<Step>('email');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [eventType, setEventType] = useState('');
  const [eventDate, setEventDate] = useState('');
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const isCpacTraffic = useCallback(() => {
    const params = new URLSearchParams(window.location.search);
    const utm = (params.get('utm') || params.get('utm_source') || '').toLowerCase();
    if (utm === 'cpac') {
      safeSet('promo_source_global', 'cpac');
      return true;
    }
    const stored = (safeGet('promo_source_global') || '').toLowerCase();
    return stored === 'cpac';
  }, []);

  const canShow = useCallback(() => {
    if (!isCpacTraffic()) return false;
    if (user) return false;
    if (safeGet(CPAC_SUBMITTED_KEY) === 'true') return false;
    const path = window.location.pathname;
    if (path.startsWith('/onboard') || path.startsWith('/admin') || path.startsWith('/order') || path === '/login') return false;
    return true;
  }, [user, isCpacTraffic]);

  useEffect(() => {
    if (safeGet(CPAC_SUBMITTED_KEY) === 'true') {
      setSubmitted(true);
      return;
    }
    const timer = setTimeout(() => { if (canShow()) setVisible(true); }, 5000);
    return () => clearTimeout(timer);
  }, [canShow]);

  const fmt = (v: string) => {
    const d = v.replace(/\D/g, '');
    if (d.length <= 3) return d;
    if (d.length <= 6) return `(${d.slice(0, 3)}) ${d.slice(3)}`;
    return `(${d.slice(0, 3)}) ${d.slice(3, 6)}-${d.slice(6, 10)}`;
  };

  const captureLead = async (payload: Record<string, any>) => {
    return fetch(`${process.env.REACT_APP_SUPABASE_URL}/functions/v1/capture-lead`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${process.env.REACT_APP_SUPABASE_ANON_KEY}`,
      },
      body: JSON.stringify(payload),
    });
  };

  const cleanPhone = (raw: string): string => {
    let digits = raw.replace(/\D/g, '');
    if (digits.length === 11 && digits.startsWith('1')) digits = digits.slice(1);
    return `+1${digits}`;
  };

  const sendCpacEmail = async (toEmail: string) => {
    const couponLink = `https://shoutout.us?utm=cpac&coupon=${COUPON_CODE}`;
    const html = `
<!DOCTYPE html>
<html>
<head><meta charset="utf-8"><meta name="viewport" content="width=device-width,initial-scale=1"></head>
<body style="margin:0;padding:0;background:#f4f4f5;font-family:-apple-system,BlinkMacSystemFont,'Segoe UI',Roboto,sans-serif;">
<table width="100%" cellpadding="0" cellspacing="0" style="background:#f4f4f5;padding:32px 16px;">
<tr><td align="center">
<table width="100%" cellpadding="0" cellspacing="0" style="max-width:520px;background:#0a0a14;border-radius:16px;overflow:hidden;">
  <!-- CPAC Logo Header -->
  <tr><td style="padding:32px 32px 8px;text-align:center;">
    <p style="color:rgba(255,255,255,0.4);font-size:11px;text-transform:uppercase;letter-spacing:2px;margin:0 0 12px;">As seen at</p>
    <img src="https://shoutout.us/cpac-logo.png" alt="CPAC" width="140" style="height:auto;display:inline-block;" />
  </td></tr>
  <!-- Body -->
  <tr><td style="padding:24px 32px 8px;text-align:center;">
    <h1 style="color:#ffffff;font-size:22px;font-weight:700;line-height:1.3;margin:0 0 16px;">
      Thanks for joining us at CPAC! 🇺🇸
    </h1>
    <p style="color:rgba(255,255,255,0.7);font-size:15px;line-height:1.5;margin:0 0 24px;">
      Here's your <strong style="color:#ef4444;">25% off</strong> coupon for a Personalized Video ShoutOut from your favorite free-speech personality.
    </p>
  </td></tr>
  <!-- Coupon Code -->
  <tr><td style="padding:0 32px;text-align:center;">
    <table width="100%" cellpadding="0" cellspacing="0" style="background:rgba(255,255,255,0.08);border-radius:12px;">
      <tr><td style="padding:20px;text-align:center;">
        <p style="color:rgba(255,255,255,0.4);font-size:12px;margin:0 0 4px;">Your Coupon Code</p>
        <p style="color:#ef4444;font-size:32px;font-family:'Courier New',monospace;font-weight:700;letter-spacing:3px;margin:0;">${COUPON_CODE}</p>
      </td></tr>
    </table>
  </td></tr>
  <!-- CTA Button -->
  <tr><td style="padding:24px 32px;text-align:center;">
    <a href="${couponLink}" style="display:inline-block;background:#dc2626;color:#ffffff;text-decoration:none;font-weight:700;font-size:16px;padding:14px 40px;border-radius:12px;">
      Browse Creators &amp; Use Your 25% Off
    </a>
  </td></tr>
  <!-- Footer -->
  <tr><td style="padding:0 32px 24px;text-align:center;">
    <p style="color:rgba(255,255,255,0.3);font-size:12px;margin:0;">Use it whenever — this coupon doesn't expire.</p>
  </td></tr>
  <!-- Red accent bar -->
  <tr><td style="height:6px;background:#dc2626;"></td></tr>
</table>
<!-- ShoutOut footer -->
<table width="100%" cellpadding="0" cellspacing="0" style="max-width:520px;">
  <tr><td style="padding:24px 16px;text-align:center;">
    <img src="https://shoutout.us/whiteicon.png" alt="ShoutOut" width="40" style="height:auto;opacity:0.4;" />
    <p style="color:#999;font-size:11px;margin:8px 0 0;">
      ShoutOut — Personalized Video Messages<br/>
      <a href="https://shoutout.us/unsubscribe?email=${encodeURIComponent(toEmail)}" style="color:#999;text-decoration:underline;">Unsubscribe</a>
    </p>
  </td></tr>
</table>
</td></tr>
</table>
</body>
</html>`;

    try {
      await supabase.functions.invoke('send-email', {
        body: {
          to: toEmail,
          subject: '🇺🇸 Your CPAC 25% Off Coupon — Use It Anytime!',
          html,
        },
      });
    } catch (err) {
      console.error('CPAC email send error:', err);
    }
  };

  const handleEmailSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) { toast.error('Enter a valid email'); return; }
    setLoading(true);
    const norm = email.toLowerCase().trim();
    safeSet('giveaway_email', norm);

    // Always capture email as a lead immediately
    captureLead({ email: norm, source: 'cpac_popup', utm_source: 'cpac' }).catch(() => {});

    try {
      const { data: existing } = await supabase.from('users').select('phone').eq('email', norm).single();
      if (existing?.phone) {
        setPhone(existing.phone);
        setStep('event');
      } else {
        setStep('phone');
      }
    } catch {
      setStep('phone');
    } finally { setLoading(false); }
  };

  const handlePhoneSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const digits = phone.replace(/\D/g, '');
    let clean = digits;
    if (digits.length === 11 && digits.startsWith('1')) clean = digits.slice(1);
    if (clean.length !== 10) { toast.error('Enter a valid 10-digit phone number'); return; }
    setPhone(`+1${clean}`);
    setStep('event');
  };

  const finalize = async (withEvent: boolean) => {
    setLoading(true);
    const norm = email.toLowerCase().trim();
    const formattedPhone = phone.startsWith('+1') ? phone : cleanPhone(phone);

    try {
      const leadPayload: Record<string, any> = {
        email: norm,
        phone: formattedPhone,
        source: 'cpac_popup',
        utm_source: 'cpac',
      };
      if (withEvent && eventType) leadPayload.upcoming_event_type = eventType;
      if (withEvent && eventDate) leadPayload.upcoming_event_date = eventDate;

      await captureLead(leadPayload);

      // Send immediate SMS with coupon link
      try {
        const couponLink = `https://shoutout.us?utm=cpac&coupon=${COUPON_CODE}`;
        await supabase.functions.invoke('send-sms', {
          body: {
            to: formattedPhone,
            message: `Thanks for joining us at CPAC! 🇺🇸 Here's your 25% off coupon for a Personalized Video ShoutOut: ${couponLink}`,
            useUserNumber: true,
          },
        });
      } catch (err) {
        console.error('CPAC SMS error:', err);
      }

      // Send welcome email with CPAC branding
      await sendCpacEmail(norm);

      // Track in beta_signups
      try {
        await supabase.from('beta_signups').upsert({
          phone_number: formattedPhone,
          source: 'cpac_popup',
          utm_source: 'cpac',
          prize_won: 'CPAC25',
          subscribed_at: new Date().toISOString(),
        }, { onConflict: 'phone_number', ignoreDuplicates: false });
      } catch {}

      safeSet('auto_apply_coupon', COUPON_CODE);
      safeSet(CPAC_SUBMITTED_KEY, 'true');
      setSubmitted(true);
      window.dispatchEvent(new Event('couponApplied'));
      setStep('done');
    } catch (err) {
      console.error('CPAC submit error:', err);
      toast.error('Something went wrong — please try again.');
    } finally { setLoading(false); }
  };

  const handleEventSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!eventType) { toast.error('Select an event type'); return; }
    if (!eventDate) { toast.error('Select a date'); return; }
    await finalize(true);
  };

  const close = () => {
    setVisible(false);
    safeSet(CPAC_CLOSED_KEY, Date.now().toString());
    if (!submitted) setShowFab(true);
  };

  const openFromFab = () => {
    setShowFab(false);
    setVisible(true);
  };

  // Don't render anything for non-cpac traffic
  if (!isCpacTraffic()) return null;

  const inputClass = 'w-full px-4 py-3 rounded-xl text-center text-lg font-medium bg-white/95 text-gray-800 focus:ring-2 focus:ring-red-400 focus:outline-none';
  const btnClass = 'w-full py-4 rounded-xl font-bold text-lg transition-all transform hover:scale-[1.02] disabled:opacity-50 disabled:cursor-not-allowed bg-red-600 hover:bg-red-700 text-white';

  return (
    <>
      {/* Floating CPAC button — shows after X-close, hidden after submit */}
      {showFab && !visible && !submitted && (
        <button
          onClick={openFromFab}
          className="fixed bottom-24 right-4 z-[10000] w-14 h-14 rounded-full bg-white shadow-lg border border-gray-200 flex items-center justify-center hover:scale-110 transition-transform md:bottom-6 md:right-6"
          title="Open CPAC offer"
        >
          <img src="/cpac-logo.png" alt="CPAC" className="w-9 h-9 object-contain" />
        </button>
      )}

      {/* Full popup */}
      {visible && (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={close} />
          <div className="relative w-full max-w-md rounded-3xl overflow-hidden shadow-2xl animate-fade-in bg-[#0a0a14]">
            <button onClick={close} className="absolute top-4 right-4 p-2 text-white/60 hover:text-white transition-colors z-10">
              <XMarkIcon className="h-6 w-6" />
            </button>

            <div className="p-8 text-center">
              {step === 'done' ? (
                <div className="py-4">
                  <div className="text-5xl mb-3">🇺🇸</div>
                  <h2 className="text-2xl font-bold text-white mb-2">You're all set!</h2>
                  <p className="text-white/70 text-sm mb-6">
                    We just texted and emailed you your 25% off coupon. Use it anytime{eventType ? ' — we\'ll also send you a reminder when your event gets close.' : '.'}
                  </p>
                  <div className="bg-white/10 rounded-xl p-4 mb-6">
                    <p className="text-white/50 text-xs mb-1">Your Coupon Code</p>
                    <p className="text-red-400 text-3xl font-mono font-bold tracking-wider">{COUPON_CODE}</p>
                  </div>
                  <button onClick={close} className={btnClass}>
                    Browse Creators
                  </button>
                </div>
              ) : (
                <>
                  {/* CPAC branding */}
                  <p className="text-white/40 text-xs uppercase tracking-widest mb-2">As seen at</p>
                  <img src="/cpac-logo.png" alt="CPAC" className="mx-auto mb-5 h-14 object-contain" />

                  <h2 className="text-xl sm:text-[22px] font-bold text-white leading-tight mb-2">
                    As a CPAC attendee you get 25% off a Personalized Video ShoutOut.
                  </h2>
                  <p className="text-white/60 text-sm mb-6">
                    Unlock your exclusive discount, <span className="underline">use it whenever.</span>
                  </p>

                  {/* Progress dots */}
                  <div className="flex justify-center gap-2 mb-5">
                    {['email', 'phone', 'event'].map((s, i) => (
                      <div key={s} className={`w-2 h-2 rounded-full transition-colors ${
                        (s === step) ? 'bg-red-500' :
                        (['email', 'phone', 'event'].indexOf(step) > i) ? 'bg-red-500/50' : 'bg-white/20'
                      }`} />
                    ))}
                  </div>

                  {/* Step 1: Email */}
                  {step === 'email' && (
                    <form onSubmit={handleEmailSubmit} className="space-y-4">
                      <input type="email" value={email} onChange={e => setEmail(e.target.value)} placeholder="Enter your email" autoComplete="email" autoFocus className={inputClass} required />
                      <button type="submit" disabled={loading} className={btnClass}>
                        {loading ? 'Checking...' : 'Claim My 25% Off'}
                      </button>
                      <p className="text-white/30 text-xs mt-2">No purchase necessary. No credit card required.</p>
                    </form>
                  )}

                  {/* Step 2: Phone */}
                  {step === 'phone' && (
                    <form onSubmit={handlePhoneSubmit} className="space-y-4">
                      <p className="text-white/60 text-sm mb-1">We'll text you the coupon</p>
                      <input type="tel" value={phone} onChange={e => setPhone(fmt(e.target.value))} placeholder="Enter your phone number" autoComplete="tel" autoFocus className={inputClass} maxLength={14} required />
                      <button type="submit" disabled={loading} className={btnClass}>
                        Continue
                      </button>
                    </form>
                  )}

                  {/* Step 3: Upcoming Event */}
                  {step === 'event' && (
                    <form onSubmit={handleEventSubmit} className="space-y-4">
                      <p className="text-white/70 text-sm mb-1">
                        Enter an upcoming event and we'll resend your coupon when it gets close.
                      </p>
                      <select value={eventType} onChange={e => setEventType(e.target.value)} className={`${inputClass} appearance-none`}>
                        <option value="">Select event type…</option>
                        {EVENT_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
                      </select>
                      <div className="relative" onClick={() => { const el = document.getElementById('cpac-date-input'); if (el) (el as any).showPicker?.(); }}>
                        <input
                          id="cpac-date-input"
                          type="date"
                          value={eventDate}
                          onChange={e => setEventDate(e.target.value)}
                          className={`${inputClass} opacity-0 absolute inset-0 w-full h-full cursor-pointer`}
                          min={new Date().toISOString().split('T')[0]}
                          required
                        />
                        <div className={`${inputClass} cursor-pointer ${eventDate ? 'text-gray-800' : 'text-gray-400'}`}>
                          {eventDate ? new Date(eventDate + 'T00:00:00').toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' }) : 'Select a date…'}
                        </div>
                      </div>
                      <button type="submit" disabled={loading || !eventType || !eventDate} className={btnClass}>
                        {loading ? 'Saving...' : 'Get My Coupon 🎉'}
                      </button>
                      <button type="button" onClick={() => finalize(false)} className="text-white/40 hover:text-white/70 text-xs underline transition-colors">
                        Skip — just send me the coupon
                      </button>
                    </form>
                  )}
                </>
              )}
            </div>

            {/* Bottom accent */}
            <div className="h-1.5 bg-red-600" />
          </div>
        </div>
      )}
    </>
  );
};

export default CPACPopup;
